/**
 * Позиции и телеметрия всего парка в одном месте.
 *
 * Раньше позиции жили внутри ConsoleMap, а телеметрия внутри ConsoleNavMenu —
 * каждый со своей подпиской и своим запросом. Сетке дронов нужно и то, и
 * другое сразу по всем устройствам, поэтому состояние поднято в Console.
 *
 * Соединение принимаем параметром, а не заводим своё: в приложении и так по
 * одному хабу на компонент, и добавлять ещё один ради этого хука нельзя.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { logger } from '../utils/logger';
import { ModemPositionData, ModemPositionListSchema } from '../api/Drone';
import {
    HubMapPositionMessage, HubMapPositionSchema,
    HubSetOnlineMessage, HubSetOnlineSchema,
    HubTelemetryMessage, HubTelemetrySchema
} from '../asp/DroneHub';
import { APIRequestGet } from '../utils/APIRequests';

export interface FleetState {
    positions: ModemPositionData[];
    /** IMEI -> последняя телеметрия. Пусто, пока аппарат молчит. */
    telemetry: Map<string, HubTelemetryMessage>;
}

function useFleetState(connection: signalR.HubConnection): FleetState {
    const [positions, setPositions] = useState<ModemPositionData[]>([]);
    const [telemetry, setTelemetry] = useState<Map<string, HubTelemetryMessage>>(new Map());

    useEffect(() => {
        APIRequestGet('/drone/getModemsPosition', {
            schema: ModemPositionListSchema,
            logSuccess: true
        }).then((result) => {
            setPositions(result.data);
        }).catch((error) => {
            // 401 разбирает интерцептор axiosInstance, он же и редиректит.
            logger.error(error);
        });
    }, []);

    const updatePosition = useCallback((msg: HubMapPositionMessage): void => {
        setPositions((prev) => {
            const exists = prev.some((item) => item.imei === msg.imei);
            if (!exists) {
                return [...prev, { imei: msg.imei, lat: msg.lat, lng: msg.lng, yaw: msg.yaw, isOnline: msg.isOnline }];
            }

            return prev.map((marker) => marker.imei === msg.imei
                ? { ...marker, lat: msg.lat, lng: msg.lng, yaw: msg.yaw, isOnline: msg.isOnline }
                : marker);
        });
    }, []);

    const setOnline = useCallback((msg: HubSetOnlineMessage): void => {
        setPositions((prev) => prev.map((marker) => marker.imei === msg.imei
            ? { ...marker, isOnline: msg.isOnline }
            : marker));
    }, []);

    const setTelem = useCallback((msg: HubTelemetryMessage): void => {
        // Новая Map на каждое сообщение: иначе мутация не дойдёт до useMemo
        // у потребителей. Двадцать записей копировать дёшево.
        setTelemetry((prev) => {
            const next = new Map(prev);
            next.set(msg.imei, msg);

            return next;
        });
    }, []);

    // Сообщения хаба проверяем схемой — так делают все остальные подписчики в
    // приложении. Сырое сообщение в state опасно: error boundary нет, и один
    // batteryRemaining не-массивом роняет всё дерево React до чёрного экрана.
    useEffect(() => {
        const onMapPosition = (message: HubMapPositionMessage): void => {
            const result = HubMapPositionSchema.safeParse(message);
            if (result.success) {
                updatePosition(result.data);
            } else {
                logger.error('MapPosition: неожиданный формат', result.error);
            }
        };

        const onSetOnline = (message: HubSetOnlineMessage): void => {
            const result = HubSetOnlineSchema.safeParse(message);
            if (result.success) {
                setOnline(result.data);
            } else {
                logger.error('SetOnline: неожиданный формат', result.error);
            }
        };

        const onTelemetry = (message: HubTelemetryMessage): void => {
            const result = HubTelemetrySchema.safeParse(message);
            if (result.success) {
                setTelem(result.data);
            } else {
                // Держим последнюю удачную телеметрию вместо падения.
                logger.error('Telemetry: неожиданный формат', result.error);
            }
        };

        connection.on('MapPosition', onMapPosition);
        connection.on('SetOnline', onSetOnline);
        connection.on('Telemetry', onTelemetry);

        return () => {
            connection.off('MapPosition', onMapPosition);
            connection.off('SetOnline', onSetOnline);
            connection.off('Telemetry', onTelemetry);
        };
    }, [connection, updatePosition, setOnline, setTelem]);

    return useMemo(() => ({ positions, telemetry }), [positions, telemetry]);
}

export default useFleetState;
